class BaseRandom
{
    public random = Math.random;

    public color(): number
    {
        const r = Math.floor(0xFF * this.random());
        const g = Math.floor(0xFF * this.random());
        const b = Math.floor(0xFF * this.random());

        return (r << 16) | (g << 8) | (b);
    }

    public range(min: number, max: number): number
    {
        return min + ((max - min) * this.random());
    }

    public pick(...args: any[]): any
    {
        return args[Math.floor(this.random() * args.length)];
    }
    
    public pickArray(arr: any[]): any
    {
        return arr[Math.floor(this.random() * arr.length)];
    }

    public pop(arr: any[]): any
    {
        const index = Math.floor(this.random() * arr.length);

        return arr.splice(index, 1)[0];
    }

    public item(obj: Record<string, unknown>): any
    {
        if (Array.isArray(obj))
        {
            return obj[Math.floor(this.random() * obj.length)];
        }
        const keys = Object.keys(obj);
        const key = keys[Math.floor(this.random() * keys.length)];

        return obj[key];
    }

    public shuffle(array: any[]): any[]
    {
        let currentIndex = array.length;
        let temporaryValue;
        let randomIndex;

        while (currentIndex !== 0)
        {
            randomIndex = Math.floor(this.random() * currentIndex);
            currentIndex -= 1;
            temporaryValue = array[currentIndex];
            array[currentIndex] = array[randomIndex];
            array[randomIndex] = temporaryValue;
        }

        return array;
    }

    public hash(length = 6): string
    {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;
        let result = '';

        for (let i = 0; i < length; i++)
        {
            result += characters.charAt(Math.floor(this.random() * charactersLength));
        }

        return result;
    }

    public pickByOdds<T>(odds: [number, T][]): T
    {
        if (!odds.length) throw new Error('[Random] Odds list provided is empty');

        // Return the single odd if list has just one item
        if (odds.length === 1) return odds[0][1];

        // Sort item by lower to high odds
        odds = odds.slice(0).sort((a, b) => a[0] - b[0]);

        // Sum of all odds provided
        const amount = odds.reduce((prev, curr) => prev + curr[0], 0);

        // Map odd entries by value range
        const oddsMap: {min: number, max: number, val: T}[] = [];
        let accumulated = 0;

        for (const odd of odds)
        {
            oddsMap.push({
                min: accumulated,
                max: accumulated + odd[0],
                val: odd[1],
            });

            accumulated += odd[0];
        }

        // Random number based on amount
        const rand = Math.random() * amount;

        for (const odd of oddsMap)
        {
            if (rand >= odd.min && rand < odd.max) return odd.val;
        }

        // There is something wrong if the function did not return any value until this point
        throw new Error('[Random] Something has gone wrong with the odds provided');
    }
}

export class SeededRandom extends BaseRandom
{
    
    private seededHash: string;
    
    constructor(hash: string = 'apples')
    {
        super();
        this.seededHash = hash;
        this.seed(hash);
    }

    public seed(hash: string): void
    {
        // From a very good answer about pseudo random numbers on stack overflow
        // https://stackoverflow.com/a/47593316
        this.random = this.mulberry32(this.xmur3(hash)());
    }
    
    public reset(){
        this.seed(this.seededHash);
    }

    protected xmur3(str: string): () => number
    {
        let h = 1779033703 ^ str.length;

        for (let i = 0; i < str.length; i++)
        {
            h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
            h = (h << 13) | (h >>> 19);
        }

        return (): number =>
        {
            h = Math.imul(h ^ (h >>> 16), 2246822507);
            h = Math.imul(h ^ (h >>> 13), 3266489909);

            return (h ^= h >>> 16) >>> 0;
        };
    }

    protected mulberry32(a: number): () => number
    {
        return ():number =>
        {
            let t = a += 0x6D2B79F5;

            t = Math.imul(t ^ (t >>> 15), t | 1);
            t ^= t + Math.imul(t ^ (t >>> 7), t | 61);

            return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        };
    }
}

export const Random = new BaseRandom();
export const RandomSeed = new SeededRandom();
